import type {
  InteractionActionId,
  InteractionResultCode,
  InteractionTargetRef,
  ServerInteractionCancelled,
  ServerInteractionCompleted,
  ServerInteractionRejected,
  ServerInteractionStarted,
} from "@endclock/shared";
import {
  selectInteractionTarget,
  interactionTargetKey,
  type InteractionCandidate,
  type InteractionTarget,
} from "./target-selection.js";

export type InteractionLifecycleState =
  | "IDLE"
  | "TARGET_AVAILABLE"
  | "STARTING"
  | "ACTIVE"
  | "COMPLETED"
  | "CANCELLED"
  | "REJECTED";

export interface InteractionTransport {
  startInteraction(requestId: string, target: InteractionTargetRef, actionId: InteractionActionId): void;
  cancelInteraction(requestId: string): void;
}

export interface InteractionOrigin {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface InteractionSnapshot {
  readonly state: InteractionLifecycleState;
  /** True only for a normal actionable target; debug fallback candidates remain false. */
  readonly targetAvailable: boolean;
  readonly targetId: string | null;
  readonly targetKind: InteractionTargetRef["kind"] | null;
  readonly targetType: string | null;
  readonly displayName: string | null;
  readonly profileId: string | null;
  readonly actionId: InteractionActionId | null;
  readonly actionLabel: string | null;
  readonly distance: number | null;
  readonly maxRange: number | null;
  readonly lineOfSight: boolean;
  readonly objectState: string | null;
  readonly revision: number;
  readonly anchor: readonly [number, number, number] | null;
  readonly activeRequestId: string | null;
  readonly progress: number;
  readonly cancelling: boolean;
  readonly feedback: string | null;
  readonly lastResult: InteractionResultCode | null;
  readonly candidateCount: number;
  readonly targetQueryMs: number;
}

interface ActiveInteraction {
  readonly requestId: string;
  readonly target: InteractionTargetRef;
  readonly actionId: InteractionActionId;
  readonly candidate: InteractionCandidate;
  startTick?: number;
  completionTick?: number;
  durationMs: number;
  distance: number;
  progress: number;
  cancelling: boolean;
}

const FEEDBACK_DURATION_MS = 1_600;

/** Client presentation lifecycle. Only matching server messages can complete an action. */
export class InteractionController {
  private lifecycleState: InteractionLifecycleState = "IDLE";
  private target: InteractionTarget | undefined;
  private debugTarget: InteractionTarget | undefined;
  private active: ActiveInteraction | undefined;
  private requestCounter = 0;
  private feedback: string | null = null;
  private feedbackUntil = 0;
  private lastResult: InteractionResultCode | null = null;
  private candidateCount = 0;
  private targetQueryMs = 0;

  constructor(private readonly transport: InteractionTransport) {}

  updateTargets(
    candidates: readonly InteractionCandidate[],
    origin: InteractionOrigin,
    facingYaw: number,
    now = performance.now(),
    queryStartedAt?: number,
  ): void {
    const measuredFrom = queryStartedAt ?? performance.now();
    this.candidateCount = candidates.length;
    const query = {
      playerX: origin.x,
      playerY: origin.y,
      playerZ: origin.z,
      facingYaw,
      ...(this.target ? { currentTargetKey: interactionTargetKey(this.target.candidate.target) } : {}),
    };
    this.target = selectInteractionTarget(candidates, query);
    this.debugTarget = this.target ?? selectInteractionTarget(candidates, {
      ...query,
      includeOccluded: true,
      ...(this.debugTarget ? { currentTargetKey: interactionTargetKey(this.debugTarget.candidate.target) } : {}),
    });
    this.targetQueryMs = performance.now() - measuredFrom;
    this.expireFeedback(now);
    if (!this.active && !this.isTransient()) {
      this.lifecycleState = this.target ? "TARGET_AVAILABLE" : "IDLE";
    }
  }

  press(sessionId: string, now = performance.now()): boolean {
    this.expireFeedback(now);
    if (this.isTransient() && this.target) {
      this.feedback = null;
      this.lifecycleState = "TARGET_AVAILABLE";
    }
    if (!sessionId || this.lifecycleState !== "TARGET_AVAILABLE" || this.active || !this.target) return false;
    const candidate = this.target.candidate;
    this.requestCounter += 1;
    const requestId = createRequestId(sessionId, this.requestCounter);
    this.active = {
      requestId,
      target: candidate.target,
      actionId: candidate.actionId,
      candidate,
      durationMs: candidate.durationMs,
      distance: this.target.distance,
      progress: 0,
      cancelling: false,
    };
    this.lifecycleState = "STARTING";
    this.feedback = null;
    this.transport.startInteraction(requestId, candidate.target, candidate.actionId);
    return true;
  }

  release(): boolean {
    return this.requestCancel();
  }

  updateActive(origin: InteractionOrigin, estimatedServerTick: number, now = performance.now()): void {
    this.expireFeedback(now);
    const active = this.active;
    if (!active) return;
    active.distance = Math.max(0, Math.hypot(
      active.candidate.anchorX - origin.x,
      active.candidate.anchorY - origin.y,
      active.candidate.anchorZ - origin.z,
    ) - active.candidate.interactionRadius);
    if (active.distance > active.candidate.maxRange) this.requestCancel();
    if (
      this.lifecycleState === "ACTIVE"
      && active.startTick !== undefined
      && active.completionTick !== undefined
      && Number.isFinite(estimatedServerTick)
    ) {
      const ticks = active.completionTick - active.startTick;
      active.progress = ticks <= 0
        ? 0.99
        : Math.max(0, Math.min(0.99, (estimatedServerTick - active.startTick) / ticks));
    }
  }

  handleStarted(message: ServerInteractionStarted): boolean {
    const active = this.matchActive(message.requestId, message.target, message.actionId);
    if (!active) return false;
    active.startTick = message.startTick;
    active.completionTick = message.completionTick;
    active.durationMs = message.durationMs;
    this.lifecycleState = "ACTIVE";
    return true;
  }

  handleCompleted(message: ServerInteractionCompleted, now = performance.now()): boolean {
    const active = this.matchActive(message.requestId, message.target, message.actionId);
    if (!active) return false;
    active.progress = 1;
    this.active = undefined;
    this.lifecycleState = "COMPLETED";
    this.lastResult = message.result;
    this.feedback = successFeedback(active.actionId);
    this.feedbackUntil = now + FEEDBACK_DURATION_MS;
    return true;
  }

  handleCancelled(message: ServerInteractionCancelled, now = performance.now()): boolean {
    const active = this.matchActive(message.requestId, message.target, message.actionId);
    if (!active) return false;
    this.active = undefined;
    this.lifecycleState = "CANCELLED";
    this.lastResult = message.result;
    this.feedback = resultFeedback(message.result);
    this.feedbackUntil = now + FEEDBACK_DURATION_MS;
    return true;
  }

  handleRejected(message: ServerInteractionRejected, now = performance.now()): boolean {
    const active = this.active;
    if (!active || active.requestId !== message.requestId) return false;
    if (message.target !== undefined && interactionTargetKey(message.target) !== interactionTargetKey(active.target)) return false;
    if (message.actionId !== undefined && message.actionId !== active.actionId) return false;
    this.active = undefined;
    this.lifecycleState = "REJECTED";
    this.lastResult = message.result;
    this.feedback = resultFeedback(message.result);
    this.feedbackUntil = now + FEEDBACK_DURATION_MS;
    return true;
  }

  reset(): void {
    this.lifecycleState = "IDLE";
    this.target = undefined;
    this.debugTarget = undefined;
    this.active = undefined;
    this.feedback = null;
    this.feedbackUntil = 0;
    this.lastResult = null;
    this.candidateCount = 0;
    this.targetQueryMs = 0;
  }

  snapshot(): InteractionSnapshot {
    const active = this.active;
    const presentedTarget = this.target ?? this.debugTarget;
    const candidate = active?.candidate ?? presentedTarget?.candidate;
    return {
      state: this.lifecycleState,
      targetAvailable: Boolean(active || this.target),
      targetId: candidate?.target.id ?? null,
      targetKind: candidate?.target.kind ?? null,
      targetType: candidate?.objectType ?? null,
      displayName: candidate?.displayName ?? null,
      profileId: candidate?.profileId ?? null,
      actionId: active?.actionId ?? candidate?.actionId ?? null,
      actionLabel: candidate?.actionLabel ?? null,
      distance: active?.distance ?? presentedTarget?.distance ?? null,
      maxRange: candidate?.maxRange ?? null,
      lineOfSight: candidate?.lineOfSight ?? false,
      objectState: candidate?.objectState ?? null,
      revision: candidate?.revision ?? 0,
      anchor: candidate
        ? [candidate.anchorX, candidate.anchorY, candidate.anchorZ] as const
        : null,
      activeRequestId: active?.requestId ?? null,
      progress: active?.progress ?? (this.lifecycleState === "COMPLETED" ? 1 : 0),
      cancelling: active?.cancelling ?? false,
      feedback: this.feedback,
      lastResult: this.lastResult,
      candidateCount: this.candidateCount,
      targetQueryMs: this.targetQueryMs,
    };
  }

  private requestCancel(): boolean {
    const active = this.active;
    if (!active || active.cancelling) return false;
    active.cancelling = true;
    this.transport.cancelInteraction(active.requestId);
    return true;
  }

  private matchActive(requestId: string, target: InteractionTargetRef, actionId: string): ActiveInteraction | undefined {
    const active = this.active;
    return active
      && active.requestId === requestId
      && interactionTargetKey(active.target) === interactionTargetKey(target)
      && active.actionId === actionId
      ? active
      : undefined;
  }

  private expireFeedback(now: number): void {
    if (!this.isTransient() || now < this.feedbackUntil) return;
    this.feedback = null;
    this.lifecycleState = this.target ? "TARGET_AVAILABLE" : "IDLE";
  }

  private isTransient(): boolean {
    return this.lifecycleState === "COMPLETED"
      || this.lifecycleState === "CANCELLED"
      || this.lifecycleState === "REJECTED";
  }
}

function createRequestId(sessionId: string, counter: number): string {
  const safeSession = sessionId.replace(/[^A-Za-z0-9_-]/g, "").slice(-32) || "session";
  return `ix_${safeSession}_${counter.toString(36)}`.slice(0, 64);
}

function successFeedback(actionId: InteractionActionId): string {
  if (actionId === "open") return "OPEN.";
  if (actionId === "access") return "ACCESS GRANTED.";
  if (actionId === "take") return "TAKEN.";
  if (actionId === "inspect") return "INSPECTED.";
  return "DONE";
}

function resultFeedback(result: InteractionResultCode): string {
  if (result === "INVENTORY_FULL") return "NO ROOM";
  if (result === "TOO_FAR" || result === "OUT_OF_RANGE") return "TOO FAR";
  if (result === "OCCLUDED") return "BLOCKED";
  if (result === "ALREADY_OPEN" || result === "STATE_CHANGED") return "ALREADY OPEN";
  if (result === "BUSY" || result === "RATE_LIMITED") return "BUSY";
  if (result === "CANCELLED") return "CANCELLED";
  return "UNAVAILABLE";
}
